import React, { useState, useEffect, useCallback, useRef } from "react";

const DEMO_SLIDES = [
  { title: "What's next?",       subtitle: "Bold ideas for a new era.",       bg: "#e8160c", color: "#fff" },
  { title: "Design for Impact",  subtitle: "Make your mark with vision.",      bg: "#111111", color: "#fff" },
  { title: "Creative Leadership",subtitle: "Lead with purpose and style.",     bg: "#f5f0e8", color: "#111" },
  { title: "Future Ready",       subtitle: "Embrace change, drive progress.",  bg: "#0f172a", color: "#e2e8f0" },
  { title: "Collaboration",      subtitle: "Together we achieve more.",        bg: "#f9fafb", color: "#111" },
  { title: "Inspiration",        subtitle: "Fuel your next big thing.",        bg: "#111111", color: "#fff" },
];

const AUTOPLAY_DELAY = 6000;

const extractDisplayExcerpt = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return "";

  // Remove Ghost custom metadata tokens while preserving human-readable description text.
  const withoutMetadata = text
    .replace(/\bcategory\s*:\s*[^|,;\n\r]+/gi, "")
    .replace(/\bposition\s*:\s*[^|,;\n\r]+/gi, "")
    .replace(/\bvenue\s*:\s*[^|,;\n\r]+/gi, "")
    .replace(/\bdate\s*:\s*[^|,;\n\r]+/gi, "")
    .replace(/[|;,]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

  return withoutMetadata;
};

const FullscreenSlider = ({ posts }) => {
  const slides = posts && posts.length
    ? posts.map(p => ({
        title:   p.title   || "",
        subtitle: extractDisplayExcerpt(p.excerpt),
        url:     p.url     || null,
        image:   p.image   || null,
        authors: p.authors || null,
      }))
    : DEMO_SLIDES;

  const [current, setCurrent] = useState(0);
  const [prev, setPrev]       = useState(null);
  const [animating, setAnimating] = useState(false);
  const [dir, setDir]         = useState(1);
  const timerRef              = useRef(null);

  const goTo = useCallback((idx, direction = 1) => {
    if (animating) return;
    setDir(direction);
    setPrev(current);
    setAnimating(true);
    setCurrent(idx);
    setTimeout(() => { setPrev(null); setAnimating(false); }, 650);
  }, [animating, current]);

  const next = useCallback(() => goTo((current + 1) % slides.length, 1),  [current, goTo, slides.length]);
  const back = useCallback(() => goTo((current - 1 + slides.length) % slides.length, -1), [current, goTo, slides.length]);

  useEffect(() => {
    timerRef.current = setInterval(next, AUTOPLAY_DELAY);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const resetTimer = () => { clearInterval(timerRef.current); timerRef.current = setInterval(next, AUTOPLAY_DELAY); };
  const handleNext = () => { resetTimer(); next(); };
  const handleBack = () => { resetTimer(); back(); };
  const handleDot  = (i) => { resetTimer(); goTo(i, i > current ? 1 : -1); };

  const slide     = slides[current];
  const prevSlide = prev !== null ? slides[prev] : null;

  const isLightTheme = typeof document !== 'undefined' &&
    (document.documentElement.getAttribute('data-theme') === 'light' ||
     (!document.documentElement.getAttribute('data-theme') &&
      !window.matchMedia('(prefers-color-scheme: dark)').matches));

  const slideColor = slide.image
    ? '#f8fafc'
    : (slide.color || (isLightTheme ? '#111827' : '#fff'));
  const prevColor = prevSlide
    ? (prevSlide.image ? '#f8fafc' : (prevSlide.color || (isLightTheme ? '#111827' : '#fff')))
    : (isLightTheme ? '#111827' : '#fff');

  const renderSlideContent = (s, col) => (
    <>
      {s.authors && <span className="fss-authors">{s.authors}</span>}
      <h2 className="fss-title">{s.title}</h2>
      {s.subtitle && <p className="fss-subtitle">{s.subtitle}</p>}
      {s.url && (
        <div className="fss-cta-wrap">
          <a href={s.url} className="fss-read-link" style={{ borderColor: col }}>
            View show
          </a>
        </div>
      )}
    </>
  );

  return (
    <div className="fss-root" style={slide.image ? {} : { background: slide.bg || "#111" }}>
      <style>{`
        .fss-root {
          position: relative;
          width: 100%;
          height: clamp(440px, 82vh, 980px);
          min-height: 440px;
          overflow: hidden;
          transition: background 0.6s cubic-bezier(.77,0,.18,1);
        }
        .fss-slide {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-end;
          padding: clamp(1.5rem, 4.5vw, 3.5rem) clamp(1.25rem, 4.5vw, 4rem);
          pointer-events: none;
          z-index: 2;
        }
        .fss-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-color: #0b1220;
          z-index: 0;
          transition: opacity 0.65s ease;
        }
        .fss-bg-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(0,0,0,0.82) 0%,
            rgba(0,0,0,0.45) 40%,
            rgba(0,0,0,0.12) 70%,
            transparent 100%
          );
          z-index: 1;
        }
        [data-theme="light"] .fss-bg-overlay {
          background: linear-gradient(
            to top,
            rgba(255,255,255,0.88) 0%,
            rgba(255,255,255,0.55) 40%,
            rgba(255,255,255,0.18) 70%,
            transparent 100%
          );
        }
        .fss-slide-inner {
          position: relative;
          z-index: 3;
          pointer-events: auto;
          max-width: min(72ch, 100%);
        }
        .fss-slide-enter {
          animation: fss-in-right 0.65s cubic-bezier(.77,0,.18,1) forwards;
        }
        .fss-slide-enter.fss-dir-back {
          animation: fss-in-left 0.65s cubic-bezier(.77,0,.18,1) forwards;
        }
        .fss-slide-exit {
          animation: fss-out-left 0.65s cubic-bezier(.77,0,.18,1) forwards;
        }
        .fss-slide-exit.fss-dir-back {
          animation: fss-out-right 0.65s cubic-bezier(.77,0,.18,1) forwards;
        }
        @keyframes fss-in-right  { from { transform:translateX(5%); opacity:0; } to { transform:translateX(0); opacity:1; } }
        @keyframes fss-in-left   { from { transform:translateX(-5%); opacity:0; } to { transform:translateX(0); opacity:1; } }
        @keyframes fss-out-left  { from { transform:translateX(0); opacity:1; } to { transform:translateX(-5%); opacity:0; } }
        @keyframes fss-out-right { from { transform:translateX(0); opacity:1; } to { transform:translateX(5%); opacity:0; } }
        .fss-counter {
          font-size: 0.75rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 700;
          margin-bottom: 1rem;
          opacity: 0.55;
        }
        .fss-authors {
          display: block;
          font-size: 0.85rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          font-weight: 600;
          opacity: 0.75;
          margin-bottom: 0.75rem;
        }
        .fss-title {
          font-size: clamp(2.1rem, 6.8vw, 5.2rem);
          font-weight: 900;
          letter-spacing: -0.04em;
          line-height: 0.95;
          margin: 0 0 0.85rem 0;
          max-width: 16ch;
        }
        .fss-subtitle {
          font-size: clamp(0.9rem, 1.6vw, 1.15rem);
          font-weight: 400;
          margin: 0 0 1rem 0;
          opacity: 0.8;
          max-width: 52ch;
          line-height: 1.5;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .fss-cta-wrap {
          display: block;
          margin-top: 0.35rem;
        }
        .fss-read-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-decoration: none;
          color: inherit;
          border: 1.5px solid currentColor;
          border-radius: 999px;
          padding: 10px 20px;
          white-space: nowrap;
          opacity: 0.95;
          background: rgba(15, 23, 42, 0.32);
          transition: opacity 0.2s, transform 0.2s, background 0.2s;
        }
        .fss-read-link:hover {
          opacity: 1;
          transform: translateY(-1px);
          background: rgba(15, 23, 42, 0.5);
        }

        [data-theme="light"] .fss-read-link {
          background: rgba(255, 255, 255, 0.75);
          color: #111827;
          border-color: rgba(17, 24, 39, 0.45) !important;
        }

        [data-theme="light"] .fss-read-link:hover {
          background: rgba(255, 255, 255, 0.9);
        }
        .fss-nav {
          position: absolute;
          bottom: clamp(1rem, 2.5vw, 2rem);
          right: clamp(1.25rem, 3vw, 3rem);
          display: flex;
          align-items: center;
          gap: 0.75rem;
          z-index: 10;
        }
        .fss-btn {
          width: 44px; height: 44px;
          border-radius: 50%;
          border: 1.5px solid currentColor;
          background: transparent;
          color: inherit;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.65;
          transition: opacity 0.2s, transform 0.2s;
          padding: 0;
          flex-shrink: 0;
        }
        .fss-btn:hover { opacity: 1; transform: scale(1.07); }
        .fss-btn:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; }
        .fss-btn svg { width: 18px; height: 18px; display: block; }
        .fss-dots {
          position: absolute;
          bottom: clamp(1rem, 2.5vw, 2rem);
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 5px;
          z-index: 10;
          padding: 0.45rem 0.7rem;
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(6px);
        }
        .fss-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: currentColor;
          opacity: 0.35;
          border: none;
          cursor: pointer;
          padding: 0;
          transition: opacity 0.25s, transform 0.25s, width 0.25s;
        }
        .fss-dot.active { opacity: 1; width: 22px; border-radius: 3px; }
        .fss-dot:hover:not(.active) { opacity: 0.65; }

        [data-theme="light"] .fss-dots {
          background: rgba(255, 255, 255, 0.78);
          color: #111827 !important;
        }
        .fss-progress {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: rgba(255,255,255,0.15);
          z-index: 10;
        }
        .fss-progress-bar {
          height: 100%;
          background: rgba(255,255,255,0.6);
          animation: fss-progress ${AUTOPLAY_DELAY}ms linear infinite;
          transform-origin: left;
        }
        @keyframes fss-progress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @media (max-width: 900px) {
          .fss-root {
            height: 70vh;
            min-height: 380px;
          }
        }

        @media (max-width: 600px) {
          .fss-root {
            height: 68vh;
            min-height: 420px;
          }
          .fss-slide {
            justify-content: flex-end;
            padding: 1rem 1rem 1.2rem;
          }
          .fss-bg {
            background-size: contain;
            background-position: center top;
          }
          .fss-bg-overlay {
            background: linear-gradient(
              to top,
              rgba(0,0,0,0.88) 0%,
              rgba(0,0,0,0.45) 42%,
              rgba(0,0,0,0.12) 74%,
              transparent 100%
            );
          }
          [data-theme="light"] .fss-bg-overlay {
            background: linear-gradient(
              to top,
              rgba(255,255,255,0.72) 0%,
              rgba(255,255,255,0.34) 42%,
              rgba(255,255,255,0.12) 74%,
              transparent 100%
            );
          }
          .fss-nav {
            display: none;
          }
          .fss-dots {
            left: 50%;
            transform: translateX(-50%);
            bottom: 0.85rem;
          }
          .fss-slide-inner {
            padding-bottom: 3.3rem;
          }
          .fss-title {
            font-size: clamp(1.6rem, 8vw, 2.5rem);
            max-width: 13ch;
          }
          .fss-subtitle {
            font-size: 0.9rem;
            margin-bottom: 0.8rem;
            -webkit-line-clamp: 2;
          }
          .fss-read-link {
            font-size: 0.78rem;
            letter-spacing: 0.09em;
            padding: 9px 16px;
          }
        }
      `}</style>

      {/* Progress bar */}
      <div className="fss-progress"><div className="fss-progress-bar" key={current} /></div>

      {/* Exiting slide */}
      {prevSlide && (
        <div
          className={`fss-slide fss-slide-exit${dir === -1 ? " fss-dir-back" : ""}`}
          style={{ color: prevColor }}
          aria-hidden="true"
        >
          {prevSlide.image && (
            <>
              <div className="fss-bg" style={{ backgroundImage: `url(${prevSlide.image})` }} />
              <div className="fss-bg-overlay" />
            </>
          )}
          <div className="fss-slide-inner">
            <span className="fss-counter">{String(prev + 1).padStart(2,"0")} / {String(slides.length).padStart(2,"0")}</span>
            {renderSlideContent(prevSlide, prevColor)}
          </div>
        </div>
      )}

      {/* Entering / current slide */}
      <div
        className={`fss-slide${animating ? ` fss-slide-enter${dir === -1 ? " fss-dir-back" : ""}` : ""}`}
        style={{ color: slideColor }}
        aria-label={`Slide ${current + 1} of ${slides.length}: ${slide.title}`}
      >
        {slide.image && (
          <>
            <div className="fss-bg" style={{ backgroundImage: `url(${slide.image})` }} />
            <div className="fss-bg-overlay" />
          </>
        )}
        <div className="fss-slide-inner">
          <span className="fss-counter">{String(current + 1).padStart(2,"0")} / {String(slides.length).padStart(2,"0")}</span>
          {renderSlideContent(slide, slideColor)}
        </div>
      </div>

      {/* Dots */}
      <div className="fss-dots" role="tablist" aria-label="Slides" style={{ color: slideColor }}>
        {slides.map((_, i) => (
          <button
            key={i}
            className={`fss-dot${i === current ? " active" : ""}`}
            onClick={() => handleDot(i)}
            role="tab"
            aria-selected={i === current}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Prev / Next */}
      <div className="fss-nav" style={{ color: slideColor }}>
        <button className="fss-btn" onClick={handleBack} aria-label="Previous slide">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button className="fss-btn" onClick={handleNext} aria-label="Next slide">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default FullscreenSlider;
